// Indicador do item ativo do menu: um único elemento que desliza entre os itens de nível 1 (Fase 17.8, Plano 10; D-17, U-09, U-28).
// A posição vem da medição do item ativo (os submenus abertos mudam a altura da lista); sem medição possível cai na regra pura
// `deslocamentoIndicador` (itens de altura fixa). A transição é de `transform` em 220ms; movimento reduzido zera a duração.
import { useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import { deslocamentoIndicador } from '../../lib/shell/menu';

export interface IndicadorAtivoMenuProps {
  /** Índice do item de nível 1 ativo na lista; negativo quando nenhum item de nível 1 está ativo (o indicador some). */
  readonly indiceAtivo: number;
  /** Altura do item quando a medição não é possível (padrão: 40px do desktop). */
  readonly alturaItem?: number;
}

const SELETOR_ITEM_ATIVO = '[data-sidebar="menu-button"][data-active="true"]';

/** Deve ser filho direto da lista de nível 1 (`ul` com `position: relative`). */
export function IndicadorAtivoMenu({ indiceAtivo, alturaItem }: IndicadorAtivoMenuProps): ReactElement {
  const ref = useRef<HTMLSpanElement>(null);
  const [pronto, setPronto] = useState(false);

  useLayoutEffect(() => {
    const elemento = ref.current;
    const lista = elemento?.parentElement;
    if (!elemento || !lista) return undefined;

    const posicionar = (): void => {
      const ativo = indiceAtivo >= 0 ? lista.querySelector<HTMLElement>(SELETOR_ITEM_ATIVO) : null;
      if (!ativo) {
        elemento.dataset.visivel = 'false';
        return;
      }
      const altura = ativo.getBoundingClientRect().height;
      if (altura > 0) {
        const topo = ativo.getBoundingClientRect().top - lista.getBoundingClientRect().top;
        elemento.style.height = `${altura}px`;
        elemento.style.transform = `translateY(${topo}px)`;
      } else {
        elemento.style.transform = deslocamentoIndicador(indiceAtivo, alturaItem);
      }
      elemento.dataset.visivel = 'true';
    };

    posicionar();
    // A primeira posição não desliza desde o topo: a transição só liga depois do primeiro quadro.
    const quadro = requestAnimationFrame(() => setPronto(true));
    // Submenus que abrem ou fecham, o rail e a troca de largura mudam a posição do item ativo.
    const observador = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(posicionar);
    observador?.observe(lista);
    return () => {
      cancelAnimationFrame(quadro);
      observador?.disconnect();
    };
  }, [indiceAtivo, alturaItem]);

  return (
    <span
      ref={ref}
      aria-hidden="true"
      data-testid="indicador-ativo-menu"
      data-visivel="false"
      data-pronto={pronto ? 'true' : 'false'}
      className="indicador-ativo-menu"
    />
  );
}
